import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Container, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import Autocomplete from "@mui/material/Autocomplete";
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CREATE_OR_UPDATE_LOGS } from '../graphql/createOrUpdateLogs';
import { GET_WELD } from '../graphql/getWeld';
import { GET_WELDERS } from '../graphql/getWelders';
import { GET_WELDERS_BY_WELD } from "../graphql/getWeldersByWeld";
import { GET_DICTIONARIES } from "../graphql/getDictionaries";

const WeldsLogForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backUrl = searchParams.get('backUrl');
  const permissions = localStorage.permissions ? JSON.parse(localStorage.permissions) : { wl_welds: { welding_logs: {}, grinding_logs: {} } };
  const [projectId] = useState(Number(useParams().projectId));
  const [elementId] = useState(Number(useParams().elementId));
  const weldIdParam = useParams().weldId;
  const [weldId] = useState(weldIdParam);
  const [baseTotalPercentage, setBaseTotalPercentage] = useState(0)
  const [weldLength, setWeldLength] = useState(0)
  const [weldNo, setWeldNo] = useState(0)
  const [completeLength, setCompleteLength] = useState(0)
  const [selectedPercent, setSelectedPercent] = useState(String(weldId).includes(',') ? 100 : null)
  const [weld, setWeld] = useState({});
  const [isOn, setIsOn] = useState(() => Cookies.get('switcherState') === 'true');
  const [logType, setLogType] = useState('WlWelds::Logs::Welding');
  const [getWelders, { data: welders, loading: queryLoading, error: queryError }] = useLazyQuery(GET_WELDERS, {
    fetchPolicy: 'no-cache'
  });
  const { data: dictionaries } = useQuery(GET_DICTIONARIES);
  const firstWeldId = String(weldId).includes(',') ? parseInt(String(weldId).split(',')[0]) : parseInt(weldId, 10);
  const { data: weldersByWeld, loading, refetch: refetchWeldersByWeld } = useQuery(GET_WELDERS_BY_WELD, {
    variables: { weldId: firstWeldId },
    skip: String(weldId).includes(',') || logType !== 'WlWelds::Logs::Welding' || !isOn,
    fetchPolicy: 'no-cache'
  });
  const [createOrUpdateLogs, { loading: mutationLoading, error: mutationError }] = useMutation(CREATE_OR_UPDATE_LOGS);
  const [getWeld] = useLazyQuery(GET_WELD);

  useEffect(() => {
    const weldIds = String(weldId).includes(',') ? String(weldId).split(',').map(id => parseInt(id.trim())) : [parseInt(weldId, 10)];

    Promise.all(weldIds.map(id =>
      getWeld({ variables: { weldId: id } })
    )).then((responses) => {
      const welds = responses.map(response => response.data?.weld).filter(Boolean);
      setWeld(welds.length === 1 ? welds[0] : welds);
      const progress = welds[0]?.welding_progress || 0;
      const length = Number(welds[0]?.length) || 0;
      setBaseTotalPercentage(progress);
      setWeldLength(length);
      setWeldNo(welds[0]?.no || 0);
      const remainingLength = Math.round(length - length * (progress / 100));
      setCompleteLength(remainingLength);
      const wps = welds[0]?.wps || [];
      setWpsList(wps);
      setWpsIds(wps.map(w => Number(w.id)));
    })
  }, [weldId, getWeld])

  // Filtruj spawaczy po załadowaniu danych WPS
  useEffect(() => {
    if (weld && Object.keys(weld).length > 0) {
      if (logType === 'WlWelds::Logs::Welding' && !String(weldId).includes(',')) {
        const allStandards = [];
        const weldsArray = Array.isArray(weld) ? weld : [weld];
        
        weldsArray.forEach(w => {
          if (w.wps) {
            w.wps.forEach(wps => {
              if (wps.welder_certification_standards) {
                allStandards.push(...wps.welder_certification_standards);
              }
            });
          }
        });
        
        let uniqueStandards = [...new Set(allStandards)];
        if (uniqueStandards.length === 0) {
          uniqueStandards = [0];
        }
        
        const weldingDate = inputs[0]?.date || getTodayDate();
        const formattedDate = weldingDate.replace('T', ' ') + ':00';
        
        getWelders({
          variables: {
            filters: {
              permission_key: 'core.people.welder',
              active: 'true',
              wps_welder_certification_standards: {
                welder_certification_standards: uniqueStandards,
                welding_date: formattedDate
              }
            }
          }
        });
      } else {
        getWelders({
          variables: {
            filters: {
              permission_key: logType === 'WlWelds::Logs::Grinding' ? 'core.people.grinder' : 'core.people.welder',
              active: 'true'
            }
          }
        });
      }
    }
  }, [weld, logType])



  const [error, setError] = useState(null);

  if (error) {
    console.error(error)
  }

  const welderList = welders?.people?.list ? welders.people.list.map((welder) => ({ id: welder.id, name: welder.name })) : [];
  const welderListByWled = weldersByWeld?.welders_by_weld ? weldersByWeld.welders_by_weld.map((welder) => ({ id: welder.id, name: welder.name })) : [];

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00`;
  };

  const [inputs, setInputs] = useState([{ welder: "", welderId: "", date: getTodayDate(), note: '' }]);
  const [weldingConsumables, setWeldingConsumables] = useState([]);
  const [wpsIds, setWpsIds] = useState([]);
  const [wpsList, setWpsList] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const [validationError, setValidationError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isMultipleWelds = String(weldId).includes(',');

  const getCompletePercent = () => {
    if (isMultipleWelds) return selectedPercent || 0;
    return Math.round((completeLength * 100) / (weldLength || 1));
  };

  const getAfterSavePercent = () => {
    const afterLength = Math.round(weldLength * (baseTotalPercentage / 100)) + completeLength;
    return Math.round((afterLength * 100) / (weldLength || 1));
  };

  const handlePercentClick = (percent) => {
    if (isMultipleWelds) {
      setSelectedPercent(percent);
    } else {
      setCompleteLength(Math.round((weldLength * percent) / 100));
    }
  };

  const handleAddInput = () => {
    setInputs((prevInputs) => [
      ...prevInputs,
      { welder: "", welderId: "", date: prevInputs[0].date, note: prevInputs[0].note },
    ]);
  };

  const handleRemoveInput = (index) => {
    setInputs((prevInputs) => prevInputs.filter((_, i) => i !== index));
  };
  const handleInputChange = (index, event) => {
    const { name, value } = event.target;
    const updatedInputs = [...inputs];

    if (name === 'welder') {
      const useQualificationFilter = logType === 'WlWelds::Logs::Welding' && isOn && !String(weldId).includes(',');
      const selectedWelder = (useQualificationFilter ? welderListByWled : welderList).find(w => w.id === value);
      if (selectedWelder) {
        updatedInputs[index] = { ...updatedInputs[index], welder: selectedWelder.name, welderId: selectedWelder.id };
      }
    } else {
      updatedInputs[index] = { ...updatedInputs[index], [name]: value };
    }

    setInputs(updatedInputs);
    setValidationError('');
  };

  const handleChangeDateOrNote = (e) => {
    const { name, value } = e.target;
    setInputs((prevInputs) =>
      prevInputs.map((input) => ({ ...input, [name]: value }))
    );
    if (name === 'date' && logType === 'WlWelds::Logs::Welding' && !String(weldId).includes(',')) {
      const allStandards = [];
      const weldsArray = Array.isArray(weld) ? weld : [weld];
      weldsArray.forEach(w => {
        if (w.wps) {
          w.wps.forEach(wps => {
            if (wps.welder_certification_standards) {
              allStandards.push(...wps.welder_certification_standards);
            }
          });
        }
      });
      let uniqueStandards = [...new Set(allStandards)];
      if (uniqueStandards.length === 0) uniqueStandards = [0];
      const formattedDate = value.replace('T', ' ') + ':00';
      getWelders({
        variables: {
          filters: {
            permission_key: 'core.people.welder',
            active: 'true',
            wps_welder_certification_standards: {
              welder_certification_standards: uniqueStandards,
              welding_date: formattedDate
            }
          }
        }
      });
    }
  };

  const handleDateBlur = (e) => {
    const value = e.target.value;
    if (logType === 'WlWelds::Logs::Welding' && !String(weldId).includes(',')) {
      const allStandards = [];
      const weldsArray = Array.isArray(weld) ? weld : [weld];
      
      weldsArray.forEach(w => {
        if (w.wps) {
          w.wps.forEach(wps => {
            if (wps.welder_certification_standards) {
              allStandards.push(...wps.welder_certification_standards);
            }
          });
        }
      });
      
      let uniqueStandards = [...new Set(allStandards)];
      if (uniqueStandards.length === 0) {
        uniqueStandards = [0];
      }
      
      const formattedDate = value.replace('T', ' ') + ':00';
      
      getWelders({
        variables: {
          filters: {
            permission_key: 'core.people.welder',
            active: 'true',
            wps_welder_certification_standards: {
              welder_certification_standards: uniqueStandards,
              welding_date: formattedDate
            }
          }
        }
      });
    }
  };

  const handleSubmit = async () => {
    const missingWelder = inputs.find(input => !input.welderId);
    if (missingWelder) {
      setValidationError('Please select a welder for all entries.');
      return;
    }
    if (logType === 'WlWelds::Logs::Welding' && wpsIds.length === 0 && !isMultipleWelds) {
      setValidationError('Please select at least one WPS.');
      return;
    }
    setIsSaving(true);
    try {
      let lastPromise = Promise.resolve();

      const completePercent = getCompletePercent();
      const mutations = inputs.map((input, index) => {
        lastPromise = lastPromise.then(async () => {
          const { welderId, date, note } = input;
          const calculateComplete = isMultipleWelds
            ? weldLength * (completePercent / 100)
            : completeLength;

          const formattedDate = (() => {
            const [datePart, timePart] = date.split('T');
            const [year, month, day] = datePart.split('-');
            return `${day}.${month}.${year} ${timePart || '00:00'}`;
          })();

          const result = await createOrUpdateLogs({
            variables: {
              person_id: welderId,
              complete: calculateComplete,
              completePercent: String(completePercent),
              date: formattedDate,
              note,
              welds_id: isMultipleWelds ? String(weldId).split(',').map(id => parseInt(id.trim())) : [Number(weldId)],
              type: logType,
              welding_consumables: weldingConsumables,
              wps_ids: logType === 'WlWelds::Logs::Welding' ? wpsIds : null
            }
          });
          return result;
        });

        return lastPromise;
      });

      await Promise.all(mutations);
      setSuccessMessage('Success!');
      if (backUrl) {
        window.location.href = atob(backUrl);
      } else {
        navigate(-1);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };
  const handleSwitcher = () => {
    setIsOn((prev) => {
      const newState = !prev;
      Cookies.set('switcherState', newState.toString());

      if (newState && !String(weldId).includes(',') && logType === 'WlWelds::Logs::Welding') {
        refetchWeldersByWeld();
      } else {
        getWelders({
          variables: {
            filters: {
              permission_key: logType === 'WlWelds::Logs::Grinding' ? 'core.people.grinder' : 'core.people.welder',
              active: 'true'
            }
          }
        });
      }

      return newState;
    });
    setInputs([{ welder: "", welderId: "", date: getTodayDate(), note: '' }]);
  };

  const handleLogTypeChange = (event) => {
    const newType = event.target.value;
    const permissionKey = newType === 'WlWelds::Logs::Grinding' ? 'core.people.grinder' : 'core.people.welder';
    setLogType(newType);
    setInputs([{ welder: "", welderId: "", date: getTodayDate(), note: '' }]);

    if (permissionKey === 'core.people.welder' && !String(weldId).includes(',')) {
      const allStandards = [];
      const weldsArray = Array.isArray(weld) ? weld : [weld];
      weldsArray.forEach(w => {
        if (w.wps) {
          w.wps.forEach(wps => {
            if (wps.welder_certification_standards) {
              allStandards.push(...wps.welder_certification_standards);
            }
          });
        }
      });
      let uniqueStandards = [...new Set(allStandards)];
      if (uniqueStandards.length === 0) uniqueStandards = [0];

      const weldingDate = inputs[0]?.date || getTodayDate();
      const formattedDate = weldingDate.replace('T', ' ') + ':00';

      getWelders({
        variables: {
          filters: {
            permission_key: 'core.people.welder',
            active: 'true',
            wps_welder_certification_standards: {
              welder_certification_standards: uniqueStandards,
              welding_date: formattedDate
            }
          }
        }
      });
    } else {
      getWelders({
        variables: {
          filters: {
            permission_key: permissionKey,
            active: 'true'
          }
        }
      });
    }
  };

  const renderWeldsAccordion = () => {
    const weldsArray = Array.isArray(weld) ? weld : [weld];

    return (
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Weld Information ({weldsArray.length} weld{weldsArray.length > 1 ? 's' : ''})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>No</TableCell>
                  <TableCell>Weld Type</TableCell>
                  <TableCell>Length</TableCell>
                  <TableCell>WPS</TableCell>
                  <TableCell>Welding Progress</TableCell>
                  <TableCell>Inspection Category</TableCell>
                  <TableCell>Inspections</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {weldsArray.map((weldItem, index) => (
                  <TableRow key={weldItem?.id || index}>
                    <TableCell>{weldItem?.no || '-'}</TableCell>
                    <TableCell>
                      {Array.isArray(weldItem?.weld_joint_type) && weldItem.weld_joint_type.length > 0 ?
                        weldItem.weld_joint_type.map((wjt, index) => {
                          if (wjt.weld_type?.name && wjt.joint_type?.name) {
                            return `${wjt.weld_type.name}-${wjt.joint_type.name}`;
                          } else if (wjt.weld_type?.name) {
                            return `${wjt.weld_type.name}`;
                          }
                          return null;
                        }).filter(Boolean).join(', ') : '-'
                      }
                    </TableCell>
                    <TableCell>{weldItem?.length || '-'}</TableCell>
                    <TableCell>
                      {Array.isArray(weldItem?.wps) && weldItem.wps.length > 0 ?
                        weldItem.wps.map((wpsItem, index) => (
                          <div key={wpsItem.id}>
                            {wpsItem.no}
                          </div>
                        )) : '-'
                      }
                    </TableCell>
                    <TableCell>{weldItem?.welding_progress ? `${weldItem.welding_progress}%` : '-'}</TableCell>
                    <TableCell>
                      {weldItem?.inspection_category?.name ? (
                        <>
                          {weldItem.inspection_category.name}
                          <br />
                          ({[...weldItem.inspection_category.inspections || []].sort((a, b) => a.type_id - b.type_id).map(inspection => {
                            const typeName = dictionaries?.inspection_category_types?.find(type => type.id === inspection.type_id)?.name || inspection.name;
                            return `${typeName} - ${inspection.range}%`;
                          }).join(', ') || ''})
                        </>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {weldItem?.inspections ? (
                        Object.entries(typeof weldItem.inspections === 'string' ? JSON.parse(weldItem.inspections) : weldItem.inspections).map(([typeId, inspectionData]) => {
                          const typeName = dictionaries?.inspection_category_types?.find(type => type.id === parseInt(typeId))?.name || `ID:${typeId}`;
                          const color = inspectionData?.positive ? 'green' : 'red';
                          const date = inspectionData?.date || 'No date';
                          return (
                            <div key={typeId} style={{ color }}>
                              {typeName} - "{date}"
                            </div>
                          );
                        })
                      ) : '-'}
                    </TableCell>
                    <TableCell>{weldItem?.status || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };
  if (queryError) return <Alert severity="error">Error: {queryError.message}</Alert>;

  return (
    <Container sx={{ paddingTop: 4 }}>
      <Typography variant="h6" gutterBottom>
        Welds Log
      </Typography>
      {(Array.isArray(weld) ? weld.length > 0 : Object.keys(weld).length > 0) && renderWeldsAccordion()}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={logType}
          label="Type"
          onChange={handleLogTypeChange}
        >
          {permissions?.wl_welds?.welding_logs?.create && (
            <MenuItem value="WlWelds::Logs::Welding">Welding</MenuItem>
          )}
          {permissions?.wl_welds?.grinding_logs?.create && (
            <MenuItem value="WlWelds::Logs::Grinding">Grinding</MenuItem>
          )}
        </Select>
      </FormControl>
      {logType === 'WlWelds::Logs::Welding' && !String(weldId).includes(',') && (
        <Box sx={{ display: 'flex', alignItems: "center", gap: "15px" }}>
          <Typography>Welders by qualifications</Typography>
          <FormControlLabel
            control={<Switch checked={isOn} onChange={handleSwitcher} />}
            label={isOn ? "On" : "Off"}
          />
        </Box>
      )}
      {mutationError && (<Alert severity="error" sx={{ marginTop: 2 }}>Error: {mutationError.message}</Alert>)}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}
      {inputs.map((input, index) => (
        <Grid container spacing={2} key={index} alignItems="center">
          <Grid item xs={12} sm={isMultipleWelds ? 6 : 4}>
            <Autocomplete
              options={logType === 'WlWelds::Logs::Welding' && isOn && !isMultipleWelds ? (welderListByWled.length === 0 ? [] : welderListByWled) : welderList}
              getOptionLabel={(option) => option.name || "No Welders by qualifications"}
              value={
                (logType === 'WlWelds::Logs::Welding' && isOn && !isMultipleWelds
                  ? welderListByWled.find(welder => welder.id === input.welderId)
                  : welderList.find(welder => welder.id === input.welderId)
                ) || null
              }
              onChange={(event, newValue) => {
                handleInputChange(index, {
                  target: { name: "welder", value: newValue ? newValue.id : "" }
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={logType === 'WlWelds::Logs::Grinding' ? 'Choose Grinder' : 'Choose Welder'}
                  fullWidth
                  required
                  sx={{ marginTop: 2 }}
                />
              )}
              noOptionsText="No Welders by qualifications"
              isOptionEqualToValue={(option, value) => option.id === value.id}
              clearOnEscape
              clearOnBlur
            />
          </Grid>
          {!isMultipleWelds && (
            <Grid item xs={12} sm={2}>
              {index !== 0 ? (
                <IconButton onClick={() => handleRemoveInput(index)} color="primary" sx={{ marginTop: 2 }}>
                  <RemoveIcon />
                </IconButton>
              ) : (
                <IconButton onClick={handleAddInput} color="primary" sx={{ marginTop: 2 }}>
                  <AddIcon />
                </IconButton>
              )}
            </Grid>
          )}
        </Grid>
      ))}

        {logType === 'WlWelds::Logs::Welding' && wpsList.length > 0 && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2">WPS</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
              {wpsList.map(wps => (
                <FormControlLabel
                  key={wps.id}
                  control={
                    <Switch
                      size="small"
                      checked={wpsIds.includes(Number(wps.id))}
                      onChange={() => {
                        const id = Number(wps.id);
                        setWpsIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
                      }}
                    />
                  }
                  label={wps.no}
                />
              ))}
            </Box>
          </Grid>
        )}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="subtitle1"><strong>Length (mm)</strong></Typography>
        {isMultipleWelds ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>You have selected multiple welds</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              {[100, 75, 50, 25].map(p => (
                <Button key={p} variant={selectedPercent === p ? 'contained' : 'outlined'} onClick={() => handlePercentClick(p)}>{p}%</Button>
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary">
              All welds will be welded at <strong>{selectedPercent || 0}%</strong> of their length
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              The weld has a length: <strong>{weldLength} mm</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Currently welded length is <strong>{Math.round(weldLength * (baseTotalPercentage / 100))} mm</strong>, which is <strong>{baseTotalPercentage}%</strong>
            </Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  type="number"
                  label="Length (mm)"
                  value={completeLength}
                  onChange={(e) => setCompleteLength(Number(e.target.value) || 0)}
                  inputProps={{ min: 0, max: weldLength }}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={9}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {[100, 75, 50, 25].map(p => (
                    <Button key={p} variant={getCompletePercent() === p ? 'contained' : 'outlined'} onClick={() => handlePercentClick(p)} sx={{ flex: 1 }}>{p}%</Button>
                  ))}
                </Box>
              </Grid>
            </Grid>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              After saving, the weld will be welded <strong>{Math.round(weldLength * (baseTotalPercentage / 100)) + completeLength} mm</strong>, which is <strong>{getAfterSavePercent()}%</strong> of its length
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', mt: 4 }}>
        <Grid item xs={12} sm={3}>
          <TextField
            type="datetime-local"
            label="Date"
            name="date"
            value={inputs[0].date}
            fullWidth
            required
            sx={{ marginTop: 2 }}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={handleChangeDateOrNote}
            onBlur={handleDateBlur}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            freeSolo
            options={dictionaries?.welding_consumables || []}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
            value={weldingConsumables.map(name => ({ name }))}
            onChange={(event, newValue) => {
              const consumables = newValue.map(v => typeof v === 'string' ? v : v.name);
              setWeldingConsumables(consumables);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Welding Consumables"
                sx={{ marginTop: 2 }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            type="text"
            label="Notes"
            name="note"
            value={inputs[0].note}
            fullWidth
            multiline
            rows={4}
            sx={{ marginTop: 4 }}
            onChange={handleChangeDateOrNote}
          />
        </Grid>
        {
          validationError && (
            <Alert severity="error" sx={{ marginTop: 2 }}>
              {validationError}
            </Alert>
          )
        }
        {
          ((!isMultipleWelds && getAfterSavePercent() <= 100 && baseTotalPercentage < 100) || isMultipleWelds) && <Button
            sx={{ marginTop: 4 }}
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        }
      </Box>
    </Container >
  );
};

export default WeldsLogForm;
